/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, Fragment, useMemo, useState } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { ax } from '@compiled/react/runtime';

import { useLayering } from '@atlaskit/layering/use-layering';
import Motion, { type MotionProps } from '@atlaskit/motion/entering/motion';
import MotionPresenceBoundary from '@atlaskit/motion/motion-presence-boundary';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type Placement, Popper } from '@atlaskit/popper/main';
import { token } from '@atlaskit/tokens';
import type { CURRENT_SURFACE_CSS_VAR } from '@atlaskit/tokens/constants';

import { RepositionOnUpdate } from './reposition-on-update';
import { type PopperWrapperProps, type PopupComponentProps } from './types';
import { useCloseManager } from './use-close-manager';
import { useFocusManager } from './use-focus-manager';

const LOCAL_CURRENT_SURFACE_CSS_VAR: typeof CURRENT_SURFACE_CSS_VAR =
	'--ds-elevation-surface-current';

const fullWidthStyles = css({ width: '100%' });

const wrapperStyles = cssMap({
	root: {
		display: 'block',
		boxSizing: 'border-box',
		zIndex: 400,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		// Resetting text color for portal content.
		// Otherwise, when rendering into the parent (not using a portal),
		// the text color can be inherited from the parent.
		color: token('color.text'),
		[LOCAL_CURRENT_SURFACE_CSS_VAR]: token('elevation.surface.overlay'),
		'&:focus': {
			outline: 'none',
		},
	},
	fullWidth: {
		width: '100%',
	},
	motion: {
		maxHeight: 'inherit',
		display: 'flex',
	},
});

const scrollableStyles = css({
	overflow: 'auto',
});

const blanketStyles = css({
	position: 'fixed',
	inset: 0,
	backgroundColor: token('color.blanket'),
});

const modalStyles = css({
	maxHeight: `calc(100vh - 2 * ${token('space.050')})`,
	position: 'fixed',
	insetBlockStart: token('space.050'),
	insetInline: token('space.050'),
});

const focusRingStyles = cssMap({
	root: {
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('border.width.focused'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
		'@media screen and (forced-colors: active), screen and (-ms-high-contrast: active)': {
			'&:focus-visible': {
				outlineStyle: 'solid',
				outlineWidth: 1,
			},
		},
	},
});

const placementMap: Record<
	Exclude<Placement, 'auto' | 'auto-start' | 'auto-end'>,
	{ enter: MotionProps['enteringAnimation']; exit: MotionProps['exitingAnimation'] }
> = {
	top: {
		enter: token('motion.popup.enter.top'),
		exit: token('motion.popup.exit.top'),
	},
	'top-start': {
		enter: token('motion.popup.enter.top'),
		exit: token('motion.popup.exit.top'),
	},
	'top-end': {
		enter: token('motion.popup.enter.top'),
		exit: token('motion.popup.exit.top'),
	},
	bottom: {
		enter: token('motion.popup.enter.bottom'),
		exit: token('motion.popup.exit.bottom'),
	},
	'bottom-start': {
		enter: token('motion.popup.enter.bottom'),
		exit: token('motion.popup.exit.bottom'),
	},
	'bottom-end': {
		enter: token('motion.popup.enter.bottom'),
		exit: token('motion.popup.exit.bottom'),
	},
	left: {
		enter: token('motion.popup.enter.left'),
		exit: token('motion.popup.exit.left'),
	},
	'left-start': {
		enter: token('motion.popup.enter.left'),
		exit: token('motion.popup.exit.left'),
	},
	'left-end': {
		enter: token('motion.popup.enter.left'),
		exit: token('motion.popup.exit.left'),
	},
	right: {
		enter: token('motion.popup.enter.right'),
		exit: token('motion.popup.exit.right'),
	},
	'right-start': {
		enter: token('motion.popup.enter.right'),
		exit: token('motion.popup.exit.right'),
	},
	'right-end': {
		enter: token('motion.popup.enter.right'),
		exit: token('motion.popup.exit.right'),
	},
};

const DefaultPopupComponent: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<PopupComponentProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => {
	const {
		shouldRenderToParent,
		shouldFitContainer,
		children,
		appearance,
		className,
		isReferenceHidden: _isReferenceHidden,
		shouldFitViewport,
		...htmlAttributes
	} = props;

	return (
		<div
			css={[
				wrapperStyles.root,
				appearance === 'UNSAFE_modal-below-sm' && modalStyles,
				// The popup creates its own scroll container when either:
				// - It is rendered in a portal
				// - It is constrained to fit into the viewport (behind a FG)
				(!shouldRenderToParent || shouldFitViewport) && scrollableStyles,
				shouldFitContainer && fullWidthStyles,
			]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
			{...htmlAttributes}
			ref={!fg('platform-dst-motion-uplift-popup') ? ref : undefined}
		>
			{children}
		</div>
	);
});

function PopperWrapper({
	xcss,
	isOpen,
	id,
	offset,
	testId,
	content,
	fallbackPlacements,
	onClose,
	boundary,
	rootBoundary,
	shouldFlip,
	placement = 'auto',
	popupComponent: PopupContainer = DefaultPopupComponent,
	autoFocus = true,
	triggerRef,
	shouldUseCaptureOnOutsideClick,
	shouldIgnoreCloseEvent,
	shouldRenderToParent,
	shouldFitContainer,
	shouldDisableFocusLock,
	shouldReturnFocus = true,
	strategy,
	role,
	label,
	titleId,
	modifiers,
	shouldFitViewport,
	appearance = 'default',
	zIndex,
}: PopperWrapperProps): JSX.Element {
	const [popupRef, setPopupRef] = useState<HTMLDivElement | null>(null);
	const [initialFocusRef, setInitialFocusRef] = useState<HTMLElement | null>(null);
	// We have cases where we need to close the Popup on Tab press, e.g. DropdownMenu
	const shouldCloseOnTab = shouldRenderToParent && shouldDisableFocusLock;
	const shouldDisableFocusTrap = role !== 'dialog';

	useFocusManager({
		initialFocusRef,
		popupRef,
		shouldCloseOnTab,
		triggerRef,
		autoFocus,
		shouldDisableFocusTrap,
		shouldReturnFocus,
	});

	useCloseManager({
		isOpen,
		onClose,
		popupRef,
		triggerRef,
		shouldUseCaptureOnOutsideClick,
		shouldIgnoreCloseEvent,
		shouldCloseOnTab,
		autoFocus,
		shouldDisableFocusTrap,
		shouldRenderToParent,
	});

	const { currentLevel } = useLayering();

	const mergedModifiers = useMemo(
		() => [
			{
				name: 'flip',
				enabled: shouldFlip,
				options: {
					rootBoundary,
					boundary,
					fallbackPlacements,
				},
			},
			...(modifiers || []),
		],
		[shouldFlip, rootBoundary, boundary, fallbackPlacements, modifiers],
	);

	return (
		<Popper
			placement={placement}
			offset={offset}
			modifiers={mergedModifiers}
			strategy={strategy}
			shouldFitViewport={shouldFitViewport}
		>
			{({ ref, style, placement, update, isReferenceHidden }) => {
				const popupContainer = (
					<PopupContainer
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className={ax([
							xcss as string,
							// @ts-expect-error: `ax` is not typed correctly
							!initialFocusRef &&
								fg('platform-design-system-apply-popup-wrapper-focus') &&
								focusRingStyles.root,
						])}
						appearance={appearance}
						id={id}
						data-ds--level={currentLevel}
						data-placement={placement}
						data-testid={testId}
						role={role}
						aria-label={label}
						aria-labelledby={titleId}
						ref={
							!fg('platform-dst-motion-uplift-popup')
								? (node: HTMLDivElement) => {
										if (node) {
											if (typeof ref === 'function') {
												ref(node);
											} else {
												(ref as React.MutableRefObject<HTMLElement>).current = node;
											}
											setPopupRef(node);
										}
									}
								: undefined
						}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={
							fg('platform-dst-motion-uplift-popup') || appearance === 'UNSAFE_modal-below-sm'
								? {}
								: style
						}
						// using tabIndex={-1} would cause a bug where Safari focuses
						// first on the browser address bar when using keyboard
						tabIndex={autoFocus ? 0 : undefined}
						shouldRenderToParent={shouldRenderToParent}
						shouldFitContainer={shouldFitContainer}
						shouldFitViewport={shouldFitViewport}
						isReferenceHidden={isReferenceHidden}
					>
						<RepositionOnUpdate update={update}>
							<MotionPresenceBoundary>
								{content({
									update,
									isOpen,
									onClose,
									setInitialFocusRef,
								})}
							</MotionPresenceBoundary>
						</RepositionOnUpdate>
					</PopupContainer>
				);
				const isAutoPlacement =
					placement === 'auto' || placement === 'auto-start' || placement === 'auto-end';
				const container = (
					<div
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={{ ...style, zIndex }}
						css={[shouldFitContainer && wrapperStyles.fullWidth]}
						// using tabIndex={-1} would cause a bug where Safari focuses
						// first on the browser address bar when using keyboard
						ref={(node: HTMLDivElement) => {
							if (node) {
								if (typeof ref === 'function') {
									ref(node);
								} else {
									(ref as React.MutableRefObject<HTMLElement>).current = node;
								}
								setPopupRef(node);
							}
						}}
						data-testid={`${testId}--container`}
					>
						{/* Don't apply motion to auto placements */}

						<Motion
							enteringAnimation={!isAutoPlacement ? placementMap[placement].enter : undefined}
							exitingAnimation={!isAutoPlacement ? placementMap[placement].exit : undefined}
							xcss={wrapperStyles.motion}
						>
							{popupContainer}
						</Motion>
					</div>
				);

				return (
					<Fragment>
						{fg('platform-dst-motion-uplift-popup') ? container : popupContainer}
						{appearance === 'UNSAFE_modal-below-sm' && <div css={blanketStyles} />}
					</Fragment>
				);
			}}
		</Popper>
	);
}

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default PopperWrapper;
